const STORAGE_KEY = 'offlineAuthState';
const OFFLINE_GRACE_MS = 7 * 24 * 60 * 60 * 1000;
const WARNING_WINDOW_MS = 24 * 60 * 60 * 1000;

const defaultState = {
  lastSuccessfulAuth: null,
  lastSubscriptionValidation: null,
  offlineAllowedUntil: null,
  warningShownAt: null,
};

function parseState(raw) {
  if (!raw) return { ...defaultState };
  try {
    const parsed = JSON.parse(raw);
    return {
      ...defaultState,
      ...parsed,
      lastSuccessfulAuth: parsed.lastSuccessfulAuth || null,
      lastSubscriptionValidation: parsed.lastSubscriptionValidation || null,
      offlineAllowedUntil: parsed.offlineAllowedUntil || null,
      warningShownAt: parsed.warningShownAt || null,
    };
  } catch {
    return { ...defaultState };
  }
}

function readState() {
  if (typeof window === 'undefined') return { ...defaultState };
  return parseState(window.localStorage.getItem(STORAGE_KEY));
}

function writeState(state) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  return state;
}

function mergeState(patch) {
  const current = readState();
  const next = {
    ...current,
    ...patch,
  };
  return writeState(next);
}

export const authOfflineService = {
  getState() {
    return readState();
  },

  clearState() {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(STORAGE_KEY);
  },

  markAuthSuccess() {
    const now = Date.now();
    return mergeState({
      lastSuccessfulAuth: now,
      offlineAllowedUntil: now + OFFLINE_GRACE_MS,
      warningShownAt: null,
    });
  },

  markSubscriptionValidated({ extendGrace = true } = {}) {
    const now = Date.now();
    const current = readState();
    const nextAllowedUntil = extendGrace
      ? now + OFFLINE_GRACE_MS
      : current.offlineAllowedUntil || now + OFFLINE_GRACE_MS;

    return mergeState({
      lastSubscriptionValidation: now,
      offlineAllowedUntil: nextAllowedUntil,
      warningShownAt: null,
    });
  },

  isOfflineGraceValid(state = readState()) {
    return Boolean(
      state.offlineAllowedUntil && Date.now() <= state.offlineAllowedUntil
    );
  },

  getOfflineAllowedUntil(state = readState()) {
    return state.offlineAllowedUntil;
  },

  getGraceRemainingMs(state = readState()) {
    if (!state.offlineAllowedUntil) return 0;
    return Math.max(0, state.offlineAllowedUntil - Date.now());
  },

  shouldWarn(state = readState()) {
    const remaining = this.getGraceRemainingMs(state);
    return remaining > 0 && remaining <= WARNING_WINDOW_MS;
  },

  hasWarned(state = readState()) {
    return Boolean(state.warningShownAt);
  },

  markWarned() {
    return mergeState({ warningShownAt: Date.now() });
  },

  isOfflineUsageAllowed(state = readState()) {
    return this.isOfflineGraceValid(state);
  },
};
